import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import includes from 'lodash/includes';

import { setCookie } from '@cian/utils/lib/browser/cookie';

import * as Analytics from '../../utils/analytics_helper';
import { matchQueryParams } from './query_params_matcher';
import { renderFilters } from './search_config';
import { useDefaultImageAnimationEnabled } from './hooks';
import { IApplicationState, TThunkDispatch } from '../../types/redux';
import { shouldNewFiltersShow } from '../../utils/filters';
import { getSearchUrl, IJsonQueryTerms, getMapUrl } from '../../reducers/modules/search/search';
import { SearchFilters } from '../../components/search_filters/search_filters';
import { SearchBlock } from '../../components/SearchBlock';
import { getFiltersConfiguration } from '../../utils/getFiltersConfiguration';
import { TypeFilter } from '../filters/type_filter';
import { BuildingTypeFilter } from '../filters/building_type_filter';
import { FiltersBackground } from '../../components/FiltersBackground';
import { HeroContainer } from '../HeroContainer';
import { selectPageSection } from '../../selectors/pageSection';
import { ESectionType } from '../../types/sectionType';
import { selectSeoData } from '../../selectors/seoData';
import { FiltersBackgroundAdBannerContainer } from '../FiltersBackgroundAdBannerContainer';

interface ISearchUrl {
  searchUrl: string;
  queryStringOrPathName: string;
}

const MSC_REGION_IDS = [-1, 1, 4593];
const SPB_REGION_IDS = [-2, 2, 4588];
const TOP_REGIONS = [...MSC_REGION_IDS, ...SPB_REGION_IDS];

function replaceSearchPath(type: string, url: string, isNewBuildingsSection: boolean) {
  const searchUrlRe = isNewBuildingsSection ? /\/newobjects\/list\// : /\/cat\.php/;
  const filtersFormUrl = isNewBuildingsSection ? '/newobjects/search/' : '/nd/search/';

  return url.replace(searchUrlRe, filtersFormUrl);
}

export const SearchBlockContainer: React.FC = () => {
  const dispatch = useDispatch<TThunkDispatch>();
  const section = useSelector(selectPageSection);
  const type = useSelector((state: IApplicationState) => state.type);
  const buildingType = useSelector((state: IApplicationState) => state.buildingType);
  const square = useSelector((state: IApplicationState) => state.square);
  const rooms = useSelector((state: IApplicationState) => state.rooms);
  const price = useSelector((state: IApplicationState) => state.price);
  const userGeo = useSelector((state: IApplicationState) => state.userGeo);
  const year = useSelector((state: IApplicationState) => state.year);
  const suburbanOfferFilter = useSelector((state: IApplicationState) => state.suburbanOfferFilter);
  const user = useSelector((state: IApplicationState) => state.user);
  const { abGroup } = useSelector((state: IApplicationState) => state.config);
  const defaultBackground = useSelector((state: IApplicationState) => state.defaultBackground);
  const seoData = useSelector(selectSeoData);

  const { actualBuildingType, buildingTypes, searchConfig } = React.useMemo(
    () => getFiltersConfiguration(buildingType, section),
    [buildingType, section],
  );

  const isNewFilters = shouldNewFiltersShow({ section });
  const isNewBuildingsSection = section === ESectionType.NewBuildings;
  const isDailyRent = section === ESectionType.DailyRent;

  const isDefaultImageAnimationEnabled = useDefaultImageAnimationEnabled(section);

  const submitSearch = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();

      const params = matchQueryParams(
        type,
        actualBuildingType,
        square,
        rooms,
        price,
        userGeo,
        year,
        isNewBuildingsSection,
        suburbanOfferFilter,
      );

      // oxlint-disable-next-line typescript/no-explicit-any
      (dispatch(getSearchUrl(params)) as any).then((url: ISearchUrl) => {
        let searchUrl = url.searchUrl;
        // istanbul ignore next
        if (
          (params.region && includes(TOP_REGIONS, (params.region as IJsonQueryTerms<number>).value[0])) ||
          isNewBuildingsSection
        ) {
          searchUrl = replaceSearchPath(params._type, searchUrl, isNewBuildingsSection);
        }

        Analytics.trackSearchEvent('button_search', section, url.queryStringOrPathName, params, user, abGroup);

        /**
         * Ставим сессионую гео-куку на основе полученных данных от гео фильтра
         */
        const { regionId, mainTownId } = userGeo.userGeoDefinitionWithGeoFilter;

        if (regionId) {
          setCookie('session_region_id', String(regionId), { secure: false });
        }

        if (mainTownId) {
          setCookie('session_main_town_region_id', String(mainTownId), { secure: false });
        }

        window.location.href = searchUrl;
      });
    },
    [
      abGroup,
      actualBuildingType,
      dispatch,
      isNewBuildingsSection,
      price,
      rooms,
      section,
      square,
      suburbanOfferFilter,
      type,
      user,
      userGeo,
      year,
    ],
  );

  const submitMapSearch = React.useCallback(
    (e: React.SyntheticEvent<HTMLElement>) => {
      e.preventDefault();

      const params = matchQueryParams(
        type,
        actualBuildingType,
        square,
        rooms,
        price,
        userGeo,
        year,
        isNewBuildingsSection,
        suburbanOfferFilter,
      );

      // oxlint-disable-next-line typescript/no-explicit-any
      (dispatch(getMapUrl(params)) as any).then((url: string) => {
        Analytics.trackSearchEvent('search_on_map', section, url, params, user, abGroup);

        Analytics.clickSearchOnMapButtonOnFilter(section, url);

        window.location.href = url;
      });
    },
    [
      abGroup,
      actualBuildingType,
      dispatch,
      isNewBuildingsSection,
      price,
      rooms,
      section,
      square,
      suburbanOfferFilter,
      type,
      user,
      userGeo,
      year,
    ],
  );

  return (
    <SearchBlock
      section={section}
      isRedesignEnabled={section === ESectionType.Index}
      isNewFilters={isNewFilters}
      fullAreaBanner={!isDailyRent && <FiltersBackgroundAdBannerContainer />}
      backgroundNode={
        <FiltersBackground
          section={section}
          defaultImageUrl={defaultBackground}
          isDefaultImageAnimationEnabled={isDefaultImageAnimationEnabled}
        />
      }
    >
      {isNewFilters ? (
        <HeroContainer />
      ) : (
        <SearchFilters
          isDobroshrift={false}
          title={seoData?.mainTitle || ''}
          onSubmit={submitSearch}
          onMapSubmit={submitMapSearch}
        >
          {!isNewBuildingsSection && (
            <>
              <TypeFilter buildingTypes={buildingTypes} searchConfig={searchConfig} />
              <BuildingTypeFilter
                buildingType={buildingType.buildingType}
                buildingTypes={buildingTypes}
                searchConfig={searchConfig}
              />
            </>
          )}
          {renderFilters(type.searchType, buildingType.buildingType, isNewBuildingsSection, searchConfig)}
        </SearchFilters>
      )}
    </SearchBlock>
  );
};
